import React from "react";
import { Quote, Star } from "lucide-react";

const TESTIMONIALS = [
  {
    name: "Sarah J.",
    location: "Durham",
    service: "Carpets",
    body:
      "Adam fitted carpets through our whole house — beautiful job, on time, tidy and a really fair price. Couldn't recommend him more.",
  },
  {
    name: "Mark R.",
    location: "Chester-le-Street",
    service: "LVT",
    body:
      "Top quality LVT in the kitchen and lounge. The finish around the stairs is spot on — proper craftsman.",
  },
  {
    name: "Pearson Lettings",
    location: "Darlington",
    service: "Landlord work",
    body:
      "We use A.Moffat across all our rentals. Reliable, fast turnarounds and the standard is consistently excellent.",
  },
  {
    name: "Lisa M.",
    location: "Consett",
    service: "Stair carpet",
    body:
      "Absolutely thrilled with our new stair carpet and runner. The tailoring around the spindles is perfection — looks like something out of a magazine.",
  },
  {
    name: "James P.",
    location: "Bishop Auckland",
    service: "Laminate",
    body:
      "Brilliant job laying laminate through three rooms. Arrived when he said he would, cleared everything away and left it spotless. Quality finish.",
  },
  {
    name: "Wendy & Dave",
    location: "Spennymoor",
    service: "Lino & Vinyl",
    body:
      "Lovely man, very honest with his advice. Fitted lino in our kitchen and utility — fast, neat and a great price compared to the bigger chains.",
  },
  {
    name: "Hartfields Care Home",
    location: "Hartlepool",
    service: "Safety flooring",
    body:
      "Safety flooring installed across two wings of our care home. Professional throughout, minimal disruption to residents — exactly what we needed.",
  },
  {
    name: "Tom H.",
    location: "Seaham",
    service: "Carpets",
    body:
      "Used Adam after a recommendation from a neighbour. The new bedroom carpets feel luxurious and the price was very reasonable. Will definitely use again.",
  },
  {
    name: "Karen W.",
    location: "Stanley",
    service: "LVT & screeding",
    body:
      "Old concrete floor screeded and beautiful herringbone LVT laid over the top. Felt like a brand-new home. Honest, friendly and clearly knows his trade.",
  },
  {
    name: "Northgate Properties",
    location: "Newton Aycliffe",
    service: "Commercial",
    body:
      "Carpet tiles fitted across a 200m² office space over a weekend. Ready for Monday morning as promised. Top class service and great communication.",
  },
  {
    name: "Andrea L.",
    location: "Peterlee",
    service: "Uplift & Vinyl",
    body:
      "Adam lifted the old flooring, sorted the subfloor and fitted new vinyl. No mess left behind and a beautiful finish. Would 100% recommend.",
  },
  {
    name: "Robert F.",
    location: "Crook",
    service: "Carpets & stairs",
    body:
      "Couldn't be happier — quality carpet, beautifully fitted on the stairs and landing. Adam's a proper grafter and a really nice bloke too.",
  },
];

function ReviewCard({ t }) {
  return (
    <figure className="shrink-0 w-[300px] sm:w-[340px] md:w-[380px] bg-white border border-moffat-line rounded-sm p-6 md:p-7 mx-3 shadow-sm">
      <Quote
        size={36}
        className="text-moffat-red/15 absolute"
        strokeWidth={1.5}
        style={{ position: "static", float: "right" }}
      />
      <div className="flex items-center gap-0.5 text-yellow-500">
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={15} fill="currentColor" strokeWidth={0} />
        ))}
      </div>
      <blockquote className="mt-3 text-moffat-text text-[14.5px] leading-relaxed min-h-[112px]">
        "{t.body}"
      </blockquote>
      <figcaption className="mt-4 pt-3 border-t border-moffat-line flex items-end justify-between gap-3">
        <div>
          <p className="font-display text-sm text-moffat-navy">
            {t.name.toUpperCase()}
          </p>
          <p className="text-[10.5px] text-moffat-muted font-semibold uppercase tracking-widest mt-0.5">
            {t.location}
          </p>
        </div>
        {t.service && (
          <span className="text-[10px] font-bold uppercase tracking-wider bg-moffat-cream text-moffat-navy px-2 py-1 rounded-sm whitespace-nowrap">
            {t.service}
          </span>
        )}
      </figcaption>
    </figure>
  );
}

export default function Testimonials() {
  // duplicate the array for seamless infinite scroll
  const track = [...TESTIMONIALS, ...TESTIMONIALS];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="section-pad bg-moffat-cream relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-12">
          <p className="font-script text-3xl text-moffat-red leading-none">
            What customers say
          </p>
          <h2 className="font-display text-4xl sm:text-5xl text-moffat-navy mt-3 uppercase">
            Reliable.<br />Professional.<br />Recommended.
          </h2>
          <div className="mt-6 flex items-center justify-center gap-1.5 text-yellow-500">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={44} fill="currentColor" strokeWidth={0} className="drop-shadow-sm" />
            ))}
          </div>
        </div>
      </div>

      {/* Marquee track — full bleed for nice edge fade */}
      <div
        data-testid="testimonials-marquee"
        className="relative w-full overflow-hidden group"
      >
        {/* Edge fades */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-12 md:w-24 z-10"
          style={{
            background:
              "linear-gradient(to right, var(--moffat-cream), transparent)",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-12 md:w-24 z-10"
          style={{
            background:
              "linear-gradient(to left, var(--moffat-cream), transparent)",
          }}
        />

        <div
          className="flex w-max animate-marquee-slow group-hover:[animation-play-state:paused]"
          aria-label="Customer reviews carousel"
        >
          {track.map((t, idx) => (
            <div key={idx} data-testid={`testimonial-${idx % TESTIMONIALS.length}`}>
              <ReviewCard t={t} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
